export type EarlyCollegeCountry = "usa" | "canada";
export type JourneyAge = 16 | 18 | 20;

export const countryLabels: Record<EarlyCollegeCountry, { label: string; flag: string }> = {
  usa: { label: "АНУ", flag: "🇺🇸" },
  canada: { label: "Канад", flag: "🇨🇦" },
};

export const journeyStages: Record<EarlyCollegeCountry, Record<JourneyAge, { kicker: string; title: string; description: string; credentials: string[] }>> = {
  usa: {
    16: {
      kicker: "16 НАСТАЙДАА ЭХЭЛ",
      title: "Ахлах сургууль + коллежийн хичээл",
      description: "16 наснаасаа АНУ-ын ахлах сургууль болон коллежийн хичээлээ зэрэг төлөвлөж, кредитээ эхлүүлнэ.",
      credentials: ["Ахлах сургууль", "Коллежийн кредит"],
    },
    18: {
      kicker: "18-ТАЙДАА ХОЁР ҮР ДҮН",
      title: "Аттестат + Associate зэрэг",
      description: "18 насандаа ахлах сургуулийн аттестат болон Associate буюу 2 жилийн коллежийн зэргээ авна.",
      credentials: ["Ахлах сургуулийн диплом", "Associate зэрэг"],
    },
    20: {
      kicker: "20 ОРЧИМ НАСАНД",
      title: "Бакалаврын зэргээ авна",
      description: "Их, дээд сургуульд 3-р курсээс үргэлжлүүлэн сураад 20 насандаа бакалаврын зэргээ авах боломжтой.",
      credentials: ["Ахлах сургуулийн диплом", "Associate зэрэг", "Бакалаврын зэрэг"],
    },
  },
  canada: {
    16: {
      kicker: "16 НАСТАЙДАА ЭХЭЛ",
      title: "Диплом + кредитээ зэрэг эхлүүлнэ",
      description: "BC ахлах сургуулийн хичээл болон их сургуульд шилжих кредитийг зэрэг эхлүүлнэ.",
      credentials: ["Кредит цуглуулж эхэлнэ"],
    },
    18: {
      kicker: "18-ТАЙДАА ХОЁР ҮР ДҮН",
      title: "Dogwood диплом + кредит",
      description: "Хөтөлбөрөөс хамааран BC Dogwood диплом болон их сургуульд шилжих кредиттэй төгсөх боломжтой.",
      credentials: ["Dogwood диплом", "Их сургуулийн кредит"],
    },
    20: {
      kicker: "20 ОРЧИМ НАСАНД",
      title: "Бакалаврын зэргээ авна",
      description: "Зөвшөөрөгдсөн кредитээ тооцуулж, бакалаврын хөтөлбөрийн шаардлагаа ханган зэргээ авна.",
      credentials: ["Dogwood диплом", "Шилжих кредит", "Бакалаврын зэрэг"],
    },
  },
};

export const earlyCollegeSchools = {
  usa: [
    {
      name: "Seattle Colleges",
      location: "Seattle, Washington",
      tuition: "$11,700",
      tuitionNote: "1 хичээлийн жил · 9 сар",
      annualTotal: "$28,500",
      totalNote: "сургалт, байр, хоол, даатгал зэрэг багтсан сургуулийн тооцоо",
      description: "16 наснаас ахлах сургуулийн диплом болон коллежийн кредитээ зэрэг ахиулах боломжтой.",
      tags: ["16+", "Ахлах сургуулийн диплом", "Seattle"],
      source: "https://intl.seattlecolleges.edu/cost",
    },
    {
      name: "Green River College",
      location: "Auburn, Washington",
      tuition: "$12,330",
      tuitionNote: "1 хичээлийн жил · 9 сар",
      annualTotal: "$23,475 + даатгал",
      totalNote: "эрүүл мэндийн даатгал улирал бүр $402 гэсэн сургуулийн мэдээлэлтэй",
      description: "16 настай сурагч сургуулиас зөвшөөрсөн айлд байрлана. 17 наснаас оюутны байрны сонголт нээгдэж болно.",
      tags: ["16+", "Айлаар байрлах", "Оюутны байр 17+"],
      source: "https://www.greenriver.edu/international/costs-payments.html",
    },
    {
      name: "Tacoma Community College",
      location: "Tacoma, Washington",
      tuition: "$11,643",
      tuitionNote: "1 хичээлийн жил · 9 сар",
      annualTotal: "$24,048",
      totalNote: "сургуулийн тооцоолсон нэг жилийн нийт зардал",
      description: "Ахлах сургуулийн диплом болон Associate зэргийн дараа их сургуульд шилжин суралцах боломжтой.",
      tags: ["16+", "Инженер", "Англи хэлний бэлтгэл"],
      source: "https://www.tacomacc.edu/costs-admission/admission-process/international/tuitioncosts",
    },
  ],
  canada: [
    {
      name: "Columbia College",
      location: "Vancouver, BC",
      tuition: "≈ CAD $17,920",
      tuitionNote: "1 хичээлийн жил · 28 кредитийн жишиг",
      annualTotal: "Тусад нь тооцно",
      totalNote: "байр, хоол, даатгал болон бусад зардлыг сонголтоор тооцно",
      description: "BC Dogwood дипломоо авахын зэрэгцээ 21 хүртэлх их сургуулийн шилжих кредит судлах боломжтой.",
      tags: ["10–12-р анги", "Dogwood диплом", "Vancouver"],
      source: "https://www.columbiacollege.ca/admissions/tuition-fees",
    },
    {
      name: "Coquitlam College",
      location: "Coquitlam, BC",
      tuition: "CAD $7,600",
      tuitionNote: "1 хичээлийн жилийн жишиг · 4 хичээл",
      annualTotal: "Тусад нь тооцно",
      totalNote: "сонгосон хичээл, байр болон бусад зардлаас хамаарна",
      description: "Ахлах сургуулийн хичээл болон их сургуулийн шилжих хөтөлбөрийг шат дараатай үргэлжлүүлэх боломжтой.",
      tags: ["Ахлах сургууль", "Их сургуулийн шилжилт", "Metro Vancouver"],
      source: "https://www.coquitlamcollege.com/tuition-fees/",
    },
    {
      name: "Alexander Academy",
      location: "Vancouver, BC",
      tuition: "CAD $24,800",
      tuitionNote: "1 хичээлийн жил · 3 улирал",
      annualTotal: "Тусад нь тооцно",
      totalNote: "байр, хоол, даатгал болон бусад зардлыг тусад нь тооцно",
      description: "BC Dogwood дипломын шаардлагыг биелүүлж, дараагийн их сургуулийн сонголтдоо бэлтгэнэ.",
      tags: ["9–12-р анги", "Dogwood диплом", "Их сургуулийн бэлтгэл"],
      source: "https://alexanderacademy.ca/admissions/tuition-fees/",
    },
  ],
} as const;

export const majorPaths = [
  { icon: "✈️", title: "Нисэхийн инженер", text: "Математик, физик, инженерийн суурь хичээлүүдээс эхэлж, Bachelor хөтөлбөрт шилжин сурах хичээлүүдээ төлөвлөнө." },
  { icon: "💻", title: "Компьютерын ухаан", text: "Программчлал, тооцоолол, өгөгдлийн бүтцийн сууриа тавьж, computer science хөтөлбөрт үргэлжлүүлнэ." },
  { icon: "⚙️", title: "Инженер", text: "Ерөнхий инженерийн сууриа тавиад механик, цахилгаан эсвэл барилгын чиглэлээ нарийвчилж болно." },
  { icon: "📊", title: "Бизнес", text: "Эдийн засаг, нягтлан бодох, статистикийн хичээлүүдийг шатлан үзэж, бизнесийн сургуульд шилжинэ." },
  { icon: "🧬", title: "Эрүүл мэнд / Pre-Med", text: "Биологи, хими, математик зэрэг анагаахын өмнөх суурь хичээлүүдээр эхэлнэ." },
  { icon: "🧠", title: "Сэтгэл судлал", text: "Сэтгэл судлал, статистик, судалгааны аргын сууриа бүрдүүлж, бакалаврын шатанд үргэлжлүүлнэ." },
];

export const earlyCollegeFaq = [
  ["Хэзээнээс бэлдэж эхлэх вэ?", "Хүүхэд явахаас дор хаяж 6–12 сарын өмнө бэлтгэж эхлэхийг зөвлөе. Энэ хугацаанд сургууль, мэргэжил, англи хэл, материал, санхүү болон байрны сонголтоо тайван төлөвлөнө."],
  ["16 настай хүүхэд үнэхээр коллежид сурч болох уу?", "Болно. АНУ-ын зарим коллеж ахлах сургуулийн диплом болон коллежийн кредитийг зэрэг ахиулах тусгай хөтөлбөртэй. Нас, анги, дүнгийн шаардлага сургууль бүрд өөр тул тус бүрээр нь шалгана."],
  ["20 насандаа бакалаврын зэрэг авах нь баталгаатай юу?", "Үгүй. Энэ нь зөв хичээл сонгож, хангалттай кредит судлан, дараагийн их сургуульд амжилттай шилжсэн үеийн жишиг хугацаа юм."],
  ["IELTS байхгүй бол элсэж болох уу?", "Зарим сургууль өөрийн түвшин тогтоох шалгалт эсвэл англи хэлний бэлтгэл санал болгодог. Манай байгууллага IELTS зэрэг шалгалтад бэлтгэх чиглэл өгч, шаардлагатай тохиолдолд хэлний курсээр дамжих боломжийг хамт төлөвлөнө."],
  ["16 настай хүүхэд хаана амьдрах вэ?", "Ихэнх тохиолдолд сургуулиас зөвшөөрсөн айлд байрлах зэрэг хяналттай хувилбар сонгодог. Насны шаардлага болон байрны дүрэм сургууль бүрд өөр."],
  ["Сургалтын төлбөр ба нийт зардал яагаад өөр вэ?", "Сургалтын төлбөр нь зөвхөн хичээлийн үнэ. Нийт зардалд байр, хоол, даатгал, ном, тээвэр болон сургуулийн бусад хураамж орж болно."],
  ["Санхүүгийн нотолгоо нь төлбөр үү?", "Үгүй. Энэ нь сургалт, амьдралын зардлаа даах боломжтойг банкны баримтаар харуулж буй нотолгоо юм."],
];

export function getJourneyStage(country: EarlyCollegeCountry, age: JourneyAge) {
  return journeyStages[country][age];
}

export function schoolCount(country: EarlyCollegeCountry) {
  return earlyCollegeSchools[country].length;
}
